import { createContext, useContext } from 'react'
import type { CSSProperties, ReactNode } from 'react'
import { Link } from 'react-router'
import styles from './CompetitionTable.module.css'

export interface CompetitionColumn {
  /** Column head, and the label each value carries once rows stack on a phone. */
  label: string
  /** Grid track — `120px`, `minmax(0, 1fr)`. */
  width: string
  /** Right-align the head and values — dates, deadlines. */
  align?: 'end'
  /**
   * Drop the column on mid-width screens (769–1100px), where the table would
   * otherwise squeeze every column. Put the same fact in the row's `subtitle`
   * so nothing is lost. It comes back, labelled, once rows stack.
   */
  collapsible?: boolean
}

export interface CompetitionTableProps {
  columns: CompetitionColumn[]
  /** `CompetitionRow`s, in the order of `columns`. */
  children: ReactNode
  /** Width of the competition column. Defaults to the widest share. */
  nameWidth?: string
  /** No mark tile in the first column — every row then omits `mark`. */
  noMark?: boolean
  'aria-label'?: string
  className?: string
}

export interface CompetitionCell {
  content: ReactNode
  /** Emphasised: heavier and brighter — a deadline that is close. */
  strong?: boolean
}

export interface CompetitionRowProps {
  /** Where the row goes. The whole row is the link. */
  to: string
  /** Logo image or initial, drawn in a square tile. */
  mark?: ReactNode
  name: ReactNode
  /** Small line under the name — org, kind, division. */
  subtitle?: ReactNode
  /** One per table column, in the same order. */
  cells: CompetitionCell[]
  /** Visible but quieter — a competition the reader cannot enter yet. */
  muted?: boolean
}

const MARK_TRACK = '44px'

// The rows need each column's label (for the stacked layout) and alignment.
// Passing them by context keeps the row API to what changes per row.
const ColumnsContext = createContext<CompetitionColumn[]>([])

function tracks(columns: CompetitionColumn[], nameWidth: string, noMark: boolean, collapse: boolean): string {
  const cells = columns.filter((c) => !collapse || !c.collapsible).map((c) => c.width)
  return [...(noMark ? [] : [MARK_TRACK]), nameWidth, ...cells].join(' ')
}

/**
 * Competitions compared on the same handful of facts.
 *
 * A grid of cards made the reader re-find the same fact in a different place
 * on every tile; a table keeps entrants, eligibility and deadline in one
 * column each. Below 768px each row becomes a card that labels its own
 * values — same markup, same order.
 *
 * The column tracks travel to the rows as custom properties on the table, so
 * a row never needs to be told the layout.
 */
export function CompetitionTable({
  columns,
  children,
  nameWidth = 'minmax(0, 2fr)',
  noMark = false,
  'aria-label': ariaLabel,
  className,
}: CompetitionTableProps) {
  const style = {
    '--ct-columns': tracks(columns, nameWidth, noMark, false),
    '--ct-columns-mid': tracks(columns, nameWidth, noMark, true),
  } as CSSProperties
  const classes = [styles.table, noMark ? styles.noMark : '', className].filter(Boolean).join(' ')

  return (
    <div className={classes} style={style} role="list" aria-label={ariaLabel}>
      <div className={styles.heads} aria-hidden="true">
        {!noMark && <span />}
        <span>Competition</span>
        {columns.map((c) => (
          <span
            key={c.label}
            className={[c.align === 'end' ? styles.end : '', c.collapsible ? styles.collapsible : ''].filter(Boolean).join(' ') || undefined}
          >
            {c.label}
          </span>
        ))}
      </div>
      <ColumnsContext.Provider value={columns}>{children}</ColumnsContext.Provider>
    </div>
  )
}

/** One competition in a {@link CompetitionTable}. */
export function CompetitionRow({ to, mark, name, subtitle, cells, muted = false }: CompetitionRowProps) {
  const columns = useContext(ColumnsContext)
  return (
    // The list item wraps the link rather than being it: role="listitem" on
    // the anchor replaced its link role, so assistive tech (and getByRole)
    // couldn't find the competition's link.
    <div role="listitem">
      <Link to={to} className={[styles.row, muted ? styles.muted : ''].filter(Boolean).join(' ')}>
        {mark !== undefined && <span className={styles.mark}>{mark}</span>}
        <span className={styles.name}>
          <span className={styles.title}>{name}</span>
          {subtitle && <span className={styles.subtitle}>{subtitle}</span>}
        </span>
        {cells.map((cell, i) => {
          const column = columns[i]
          const classes = [
            styles.cell,
            column?.align === 'end' ? styles.end : '',
            column?.collapsible ? styles.collapsible : '',
            cell.strong ? styles.strong : '',
          ].filter(Boolean).join(' ')
          return (
            <span key={column?.label ?? i} className={classes} data-label={column?.label}>
              {cell.content}
            </span>
          )
        })}
      </Link>
    </div>
  )
}
